// Metric metadata for the Company Performance report.
//
// Driving the table columns from a single declarative list keeps tooltips,
// column groups, heatmap bands and KPI deltas all in one place — and means
// the regular and expandable tables share rendering instead of duplicating
// 30+ hand-written cells.

const safeDiv = (numerator, denominator) => {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!Number.isFinite(n) || !Number.isFinite(d) || !d) {
    return 0;
  }
  return n / d;
};

// Display order matters — these dictate column order in the tables.
export const METRIC_GROUPS = [
  { id: 'volume', label: 'Volume' },
  { id: 'leadQuality', label: 'Lead Quality' },
  { id: 'booking', label: 'Booking' },
  { id: 'issues', label: 'Issues' },
  { id: 'demos', label: 'Demos' },
  { id: 'sales', label: 'Sales' },
  { id: 'economics', label: 'Economics' },
];

// Heatmap thresholds are expressed as fractions (0.25 = 25%). They are tuned
// defaults — the team can adjust as we learn what "healthy" looks like.
//
//   polarity: 'higher-better' → below `lower` is alert, above `upper` is good
//   polarity: 'lower-better'  → above `upper` is alert, below `lower` is good
//   between thresholds → 'watch'
const HM = (lower, upper, polarity = 'higher-better') => ({ lower, upper, polarity });

// Every column rendered in PerformanceSummaryTable / ExpandablePerformanceSummaryTable.
// `select(metrics)` reads from the output of getDisplayMetrics(row, showFee).
export const METRIC_COLUMNS = [
  // Volume
  {
    id: 'spend',
    label: 'Spend',
    feeLabel: 'Spend (incl. fees)',
    group: 'volume',
    format: 'currency',
    tooltip:
      'Total media spend for the period. With "Include fees" on, each client\'s own agency fee is applied first (computed in SQL).',
    select: (m) => m.spend,
  },
  {
    id: 'leads',
    label: 'Leads',
    group: 'volume',
    format: 'number',
    tooltip: 'Total lead volume across all matching clients.',
    select: (m) => m.leads,
  },
  {
    id: 'cpl',
    label: 'CPL',
    group: 'volume',
    format: 'currency',
    tooltip: 'Cost per lead — Spend ÷ Leads.',
    select: (m) => safeDiv(m.spend, m.leads),
  },

  // Lead Quality
  {
    id: 'selfBook',
    label: 'Self Book',
    group: 'leadQuality',
    format: 'number',
    tooltip: 'Leads who booked themselves without staff intervention.',
    select: (m) => m.selfBook,
  },
  {
    id: 'cpSelfBook',
    label: 'CP Self Book',
    group: 'leadQuality',
    format: 'currency',
    tooltip: 'Cost per self-book — Spend ÷ Self Book.',
    select: (m) => safeDiv(m.spend, m.selfBook),
  },
  {
    id: 'selfBookRate',
    label: 'Self Book Rate',
    group: 'leadQuality',
    format: 'percent',
    tooltip: 'Share of leads that self-booked.',
    select: (m) => safeDiv(m.selfBook, m.leads),
    heatmap: HM(0.10, 0.25),
  },
  {
    id: 'accepted',
    label: 'Accepted',
    group: 'leadQuality',
    format: 'number',
    tooltip: 'Leads accepted by the client (not rejected).',
    select: (m) => m.accepted,
  },
  {
    id: 'acceptedRate',
    label: 'Accepted Rate',
    group: 'leadQuality',
    format: 'percent',
    tooltip: 'Share of leads accepted by the client.',
    select: (m) => safeDiv(m.accepted, m.leads),
    heatmap: HM(0.60, 0.80),
  },
  {
    id: 'success',
    label: 'Success',
    group: 'leadQuality',
    format: 'number',
    tooltip: 'Leads marked as success in the client\'s CRM / backend.',
    select: (m) => m.success,
  },
  {
    id: 'successRate',
    label: 'Success Rate',
    group: 'leadQuality',
    format: 'percent',
    tooltip: 'Success ÷ Leads.',
    select: (m) => safeDiv(m.success, m.leads),
    heatmap: HM(0.40, 0.70),
  },
  {
    id: 'outOfArea',
    label: 'Out of Area',
    group: 'leadQuality',
    format: 'number',
    tooltip: 'Leads that came in from outside the targeted service area.',
    select: (m) => m.outOfArea,
  },
  {
    id: 'outOfAreaRate',
    label: 'Out of Area Rate',
    group: 'leadQuality',
    format: 'percent',
    tooltip: 'Share of leads from outside the service area. Lower is better.',
    select: (m) => safeDiv(m.outOfArea, m.leads),
    heatmap: HM(0.05, 0.15, 'lower-better'),
  },

  // Booking
  {
    id: 'booked',
    label: 'Booked',
    group: 'booking',
    format: 'number',
    tooltip: 'Appointments booked.',
    select: (m) => m.booked,
  },
  {
    id: 'cpBooked',
    label: 'CP Booked',
    group: 'booking',
    format: 'currency',
    tooltip: 'Cost per booked appointment — Spend ÷ Booked.',
    select: (m) => safeDiv(m.spend, m.booked),
  },
  {
    id: 'setRate',
    label: 'Set Rate',
    group: 'booking',
    format: 'percent',
    tooltip: 'Share of leads that turned into a booked appointment.',
    select: (m) => safeDiv(m.booked, m.leads),
    heatmap: HM(0.20, 0.40),
  },

  // Issues
  {
    id: 'issues',
    label: 'Issues',
    group: 'issues',
    format: 'number',
    tooltip: 'Appointments where the lead actually showed up / engaged ("issued").',
    select: (m) => m.issues,
  },
  {
    id: 'cpIssue',
    label: 'CP Issue',
    group: 'issues',
    format: 'currency',
    tooltip: 'Cost per issued appointment — Spend ÷ Issues.',
    select: (m) => safeDiv(m.spend, m.issues),
  },
  {
    id: 'issueRate',
    label: 'Issue Rate',
    group: 'issues',
    format: 'percent',
    tooltip:
      'Issues ÷ Scheduled — how many scheduled appointments actually showed up. Higher is better, despite the name.',
    select: (m) => safeDiv(m.issues, m.scheduled),
    heatmap: HM(0.50, 0.75),
  },
  {
    id: 'leadToIssue',
    label: 'Lead To Issue',
    group: 'issues',
    format: 'percent',
    tooltip: 'Share of leads that became an issued appointment.',
    select: (m) => safeDiv(m.issues, m.leads),
  },

  // Demos
  {
    id: 'demos',
    label: 'Demos',
    group: 'demos',
    format: 'number',
    tooltip: 'Demo / pitch appointments completed.',
    select: (m) => m.demos,
  },
  {
    id: 'cpDemo',
    label: 'CP Demo',
    group: 'demos',
    format: 'currency',
    tooltip: 'Cost per demo — Spend ÷ Demos.',
    select: (m) => safeDiv(m.spend, m.demos),
  },
  {
    id: 'demoRate',
    label: 'Demo Rate',
    group: 'demos',
    format: 'percent',
    tooltip: 'Demos ÷ Issues — how many issued appointments became demos.',
    select: (m) => safeDiv(m.demos, m.issues),
    heatmap: HM(0.40, 0.60),
  },

  // Sales
  {
    id: 'grossSold',
    label: 'Gross Sold',
    group: 'sales',
    format: 'number',
    tooltip: 'Gross sales recorded.',
    select: (m) => m.grossSold,
  },
  {
    id: 'netSold',
    label: 'Net Sold',
    group: 'sales',
    format: 'number',
    tooltip: 'Net sales (gross minus cancellations / refunds).',
    select: (m) => m.netSold,
  },
  {
    id: 'netCloseRate',
    label: 'Net Close Rate',
    group: 'sales',
    format: 'percent',
    tooltip: 'Net Sold ÷ Demos.',
    select: (m) => safeDiv(m.netSold, m.demos),
    heatmap: HM(0.20, 0.35),
  },

  // Economics
  {
    id: 'netAmount',
    label: 'Net Amount',
    group: 'economics',
    format: 'currency',
    tooltip: 'Total net revenue / contract value.',
    select: (m) => m.netAmount,
  },
  {
    id: 'avgDeal',
    label: 'Avg Deal',
    group: 'economics',
    format: 'currency',
    tooltip: 'Average net deal size — Net Amount ÷ Net Sold.',
    select: (m) => safeDiv(m.netAmount, m.netSold),
  },
  {
    id: 'revenuePerLead',
    label: 'Revenue per Lead',
    group: 'economics',
    format: 'currency',
    tooltip: 'Net Amount ÷ Leads.',
    select: (m) => safeDiv(m.netAmount, m.leads),
  },
  {
    id: 'costOfMarketing',
    label: 'Cost of Marketing',
    group: 'economics',
    format: 'percent',
    tooltip: 'Spend ÷ Net Amount. Lower is better.',
    select: (m) => safeDiv(m.spend, m.netAmount),
    heatmap: HM(0.15, 0.30, 'lower-better'),
  },
];

// Total columns in each METRIC_GROUPS group — used for the upper header row's
// colSpan. Computed once at module load.
export const COLUMN_GROUP_SPANS = METRIC_GROUPS.map((group) => ({
  ...group,
  span: METRIC_COLUMNS.filter((column) => column.group === group.id).length,
}));

export function getHeatmapBand(value, heatmap) {
  if (!heatmap || !Number.isFinite(value)) {
    return null;
  }
  const { lower, upper, polarity } = heatmap;
  const inverted = polarity === 'lower-better';
  if (value <= lower) return inverted ? 'good' : 'alert';
  if (value >= upper) return inverted ? 'alert' : 'good';
  return 'watch';
}

// Subtle background tints — readable but doesn't overpower the number.
export const HEATMAP_BG = {
  good: 'rgba(46, 160, 67, 0.10)',
  watch: 'rgba(245, 159, 0, 0.12)',
  alert: 'rgba(218, 54, 51, 0.10)',
};

// The hero strip — pick the 6 metrics most often glanced at first.
export const KPI_DEFINITIONS = [
  {
    id: 'spend',
    label: 'Spend',
    feeLabel: 'Spend (incl. fees)',
    format: 'currency',
    polarity: 'higher-better',
    select: (m) => m.spend,
  },
  {
    id: 'leads',
    label: 'Leads',
    format: 'number',
    polarity: 'higher-better',
    select: (m) => m.leads,
  },
  {
    id: 'cpl',
    label: 'CPL',
    format: 'currency',
    polarity: 'lower-better',
    select: (m) => safeDiv(m.spend, m.leads),
  },
  {
    id: 'booked',
    label: 'Booked',
    format: 'number',
    polarity: 'higher-better',
    select: (m) => m.booked,
  },
  {
    id: 'demos',
    label: 'Demos',
    format: 'number',
    polarity: 'higher-better',
    select: (m) => m.demos,
  },
  {
    id: 'netSold',
    label: 'Net Sold',
    format: 'number',
    polarity: 'higher-better',
    select: (m) => m.netSold,
  },
];

export function getDeltaTone(delta, polarity) {
  if (!Number.isFinite(delta) || Math.abs(delta) < 0.005) {
    return 'neutral';
  }
  const isUp = delta > 0;
  if (polarity === 'lower-better') {
    return isUp ? 'bad' : 'good';
  }
  return isUp ? 'good' : 'bad';
}

// ---------------------------------------------------------------------------
// Date presets — used by the controls bar.
// All ranges are end-of-day on the most recent day in scope.
// ---------------------------------------------------------------------------

function setEndOfDay(date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

function setStartOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function daysAgo(n, anchor = new Date()) {
  const next = new Date(anchor);
  next.setDate(next.getDate() - n);
  return setStartOfDay(next);
}

export const DATE_PRESETS = [
  {
    id: '7d',
    label: '7D',
    getRange: () => ({ start: daysAgo(6), end: setEndOfDay(new Date()) }),
  },
  {
    id: '30d',
    label: '30D',
    getRange: () => ({ start: daysAgo(29), end: setEndOfDay(new Date()) }),
  },
  {
    id: 'mtd',
    label: 'MTD',
    getRange: () => {
      const now = new Date();
      return {
        start: setStartOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        end: setEndOfDay(now),
      };
    },
  },
  {
    id: 'lastMonth',
    label: 'Last month',
    getRange: () => {
      const now = new Date();
      return {
        start: setStartOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        end: setEndOfDay(new Date(now.getFullYear(), now.getMonth(), 0)),
      };
    },
  },
  {
    id: 'qtd',
    label: 'QTD',
    getRange: () => {
      const now = new Date();
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      return {
        start: setStartOfDay(new Date(now.getFullYear(), quarterStartMonth, 1)),
        end: setEndOfDay(now),
      };
    },
  },
  {
    id: 'ytd',
    label: 'YTD',
    getRange: () => {
      const now = new Date();
      return {
        start: setStartOfDay(new Date(now.getFullYear(), 0, 1)),
        end: setEndOfDay(now),
      };
    },
  },
];

// Best-effort match of the active date range against a preset for active-state
// chip highlighting. Returns the matching preset id, or null.
export function detectActivePreset(startDate, endDate) {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    return null;
  }
  for (const preset of DATE_PRESETS) {
    const range = preset.getRange();
    if (
      sameDay(range.start, startDate) &&
      sameDay(range.end, endDate)
    ) {
      return preset.id;
    }
  }
  return null;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// Compute the equivalent previous period for KPI deltas.
// Length-preserving: a 30-day current window → the 30 days immediately before.
export function getPreviousPeriod(startDate, endDate) {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    return null;
  }
  const oneDayMs = 24 * 60 * 60 * 1000;
  const spanDays = Math.max(
    1,
    Math.round((endDate.getTime() - startDate.getTime()) / oneDayMs) + 1
  );
  const prevEnd = new Date(startDate);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - (spanDays - 1));
  return {
    start: setStartOfDay(prevStart),
    end: setEndOfDay(prevEnd),
  };
}
